/**
 * Server functions لصور الهيرو العلويّ لكلّ صفحة عامّة.
 *
 * منفصل عن `page_backgrounds` (الذي يخصّ خلفيّة الصفحة تحت المحتوى).
 * يحلّ محلّ صورة fallback المُمرَّرة للـ `<PageHero pageKey="..." />` عند الإعداد.
 *
 * البنية في app_settings.value تحت key="page_hero_images":
 *   { [pageKey]: "https://..." }
 */
import { createServerFn } from "@tanstack/react-start";
import { lazyValidator } from "@/lib/server/lazy-validator";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./adminAuth.server";
// مفاتيح الصفحات التي تستعمل <PageHero pageKey=...> فعليّاً
export const HERO_PAGE_KEYS = [
  "shop",
  "about",
  "journal",
  "contact",
  "ambassadors",
  "ambassadors-tier",
  "gifts",
  "collection-large",
  "collection-ladybag",
  "cart",
  "product",
  "account",
  "wishlist",
] as const;
export type HeroPageKey = (typeof HERO_PAGE_KEYS)[number];
export const HERO_DEVICE_KEYS = ["desktop", "mobile"] as const;
export type HeroDeviceKey = (typeof HERO_DEVICE_KEYS)[number];

export const HERO_DISABLED_SENTINEL = "__disabled__";

const KEY = "page_hero_images";
const POSITIONS_KEY = "page_hero_positions";

export const HERO_POSITIONS = [
  "top-start", "top-center", "top-end",
  "middle-start", "middle-center", "middle-end",
  "bottom-start", "bottom-center", "bottom-end",
] as const;
export type HeroPosition = (typeof HERO_POSITIONS)[number];

/**
 * مفاتيح ديناميكيّة: بانر خاصّ بمنتج واحد → `product:<handle>`.
 * تُخزَّن في نفس مفاتيح app_settings (لا مصدر حقيقة ثانٍ).
 */
export function productHeroKey(handle: string): string {
  return `product:${handle}`;
}

function extractPublicMediaPath(url: string | undefined | null): string | null {
  if (!url) return null;
  const m = url.match(/\/storage\/v1\/object\/public\/public-media\/([^?#]+)/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

export const getPageHeroImages = createServerFn({ method: "GET" }).handler(async () => {
  const res = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", KEY)
    .maybeSingle();
  const map: Record<string, string> = {};
  const mobileMap: Record<string, string> = {};
  const raw = res.data?.value;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === "string" && v.trim()) map[k] = v.trim();
      else if (v && typeof v === "object" && !Array.isArray(v)) {
        const desktop = (v as Record<string, unknown>).desktop;
        const mobile = (v as Record<string, unknown>).mobile;
        if (typeof desktop === "string" && desktop.trim()) map[k] = desktop.trim();
        if (typeof mobile === "string" && mobile.trim()) mobileMap[k] = mobile.trim();
      }
    }
  }
  return { map, mobileMap };
});

export const setPageHeroImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./pageHeroImages.schemas").then((m) => m.heroMediaSchema)))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const cur = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", KEY)
      .maybeSingle();
    const map: Record<string, { desktop?: string; mobile?: string }> = {};
    const raw = cur.data?.value;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof v === "string" && v.trim()) map[k] = { desktop: v.trim() };
        else if (v && typeof v === "object" && !Array.isArray(v)) {
          const desktop = (v as Record<string, unknown>).desktop;
          const mobile = (v as Record<string, unknown>).mobile;
          map[k] = {
            ...(typeof desktop === "string" && desktop.trim() ? { desktop: desktop.trim() } : {}),
            ...(typeof mobile === "string" && mobile.trim() ? { mobile: mobile.trim() } : {}),
          };
        }
      }
    }
    const newUrl = (data.url ?? "").trim();
    const oldUrl = map[data.pageKey]?.[data.device] ?? "";
    if (!newUrl) {
      if (map[data.pageKey]) delete map[data.pageKey][data.device];
      if (map[data.pageKey] && Object.keys(map[data.pageKey]).length === 0) delete map[data.pageKey];
    } else {
      map[data.pageKey] = { ...map[data.pageKey], [data.device]: newUrl };
    }
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: KEY,
        value: map,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);

    // احذف الصورة القديمة من Storage إن لم تعد مستعملة
    const oldPath = extractPublicMediaPath(oldUrl);
    const newPath = extractPublicMediaPath(newUrl);
    if (oldPath && oldPath !== newPath) {
      const stillUsed = Object.values(map).some(
        (entry) => Object.values(entry).some((u) => extractPublicMediaPath(u) === oldPath),
      );
      if (!stillUsed) {
        await supabaseAdmin.storage.from("public-media").remove([oldPath]).catch(() => {});
      }
    }
    return { ok: true, map };
  });

// ملاحظة: نظام «التخطيط الجاهز» للهيرو أُلغي كلّياً — ارتفاع الهيرو موحَّد
// في كلّ الصفحات ومطابق لهيرو الهوم (شاشة كاملة على الموبايل).



// ────────────────────────────────────────────────────────────
// موضع النصّ والـCTA داخل الهيرو لكلّ صفحة (٩ مواقع)
export const getPageHeroPositions = createServerFn({ method: "GET" }).handler(async () => {
  const res = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", POSITIONS_KEY)
    .maybeSingle();
  const map: Record<string, HeroPosition> = {};
  const raw = res.data?.value;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === "string" && (HERO_POSITIONS as readonly string[]).includes(v)) {
        map[k] = v as HeroPosition;
      }
    }
  }
  return { map };
});

export const setPageHeroPosition = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./pageHeroImages.schemas").then((m) => m.heroPositionSchema)))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const cur = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", POSITIONS_KEY)
      .maybeSingle();
    const map: Record<string, string> = {};
    const raw = cur.data?.value;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof v === "string") map[k] = v;
      }
    }
    map[data.pageKey] = data.position;
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: POSITIONS_KEY,
        value: map,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);
    return { ok: true, map };
  });

// ────────────────────────────────────────────────────────────
// صورة غلاف (poster) لفيديو الهيرو — لكلّ صفحة على حدة.
// تُعرض قبل تشغيل الفيديو على الموبايل وكـposter على الديسكتوب.
// المصدر الوحيد في PageHero هو غلاف الصفحة؛ لا poster عامّ ولا صورة كود.
const POSTERS_KEY = "page_hero_posters";

export const getPageHeroPosters = createServerFn({ method: "GET" }).handler(async () => {
  const res = await supabaseAdmin
    .from("app_settings")
    .select("value")
    .eq("key", POSTERS_KEY)
    .maybeSingle();
  const map: Record<string, string> = {};
  const mobileMap: Record<string, string> = {};
  const raw = res.data?.value;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === "string" && v.trim()) map[k] = v.trim();
      else if (v && typeof v === "object" && !Array.isArray(v)) {
        const desktop = (v as Record<string, unknown>).desktop;
        const mobile = (v as Record<string, unknown>).mobile;
        if (typeof desktop === "string" && desktop.trim()) map[k] = desktop.trim();
        if (typeof mobile === "string" && mobile.trim()) mobileMap[k] = mobile.trim();
      }
    }
  }
  return { map, mobileMap };
});

export const setPageHeroPoster = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./pageHeroImages.schemas").then((m) => m.heroMediaSchema)))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const cur = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", POSTERS_KEY)
      .maybeSingle();
    const map: Record<string, { desktop?: string; mobile?: string }> = {};
    const raw = cur.data?.value;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
        if (typeof v === "string" && v.trim()) map[k] = { desktop: v.trim() };
        else if (v && typeof v === "object" && !Array.isArray(v)) {
          const desktop = (v as Record<string, unknown>).desktop;
          const mobile = (v as Record<string, unknown>).mobile;
          map[k] = {
            ...(typeof desktop === "string" && desktop.trim() ? { desktop: desktop.trim() } : {}),
            ...(typeof mobile === "string" && mobile.trim() ? { mobile: mobile.trim() } : {}),
          };
        }
      }
    }
    const oldUrl = map[data.pageKey]?.[data.device] ?? "";
    const newUrl = (data.url ?? "").trim();
    if (!newUrl) {
      if (map[data.pageKey]) delete map[data.pageKey][data.device];
      if (map[data.pageKey] && Object.keys(map[data.pageKey]).length === 0) delete map[data.pageKey];
    } else map[data.pageKey] = { ...map[data.pageKey], [data.device]: newUrl };
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: POSTERS_KEY,
        value: map,
        updated_by: context.userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);

    const oldPath = extractPublicMediaPath(oldUrl);
    const newPath = extractPublicMediaPath(newUrl);
    if (oldPath && oldPath !== newPath) {
      const stillUsed = Object.values(map).some(
        (entry) => Object.values(entry).some((u) => extractPublicMediaPath(u) === oldPath),
      );
      if (!stillUsed) {
        await supabaseAdmin.storage.from("public-media").remove([oldPath]).catch(() => {});
      }
    }
    return { ok: true, map };
  });
